import { useTranslation } from "react-i18next";
import { IconInfoCircle } from "@tabler/icons-react";

import { ErrorMsg, Text } from "@fragments";

/**
 * The copy for one flow message, or null when the kind is unknown. Shared by
 * the visible line below and by the code step's screen-reader announcer, so
 * both say the same thing.
 *
 * @param {function} t the `common` namespace translator
 * @param {{kind: string, tone: 'error'|'info', attemptsLeft?: number}} message
 * @param {number} [resendIn] countdown, for the rate-limit message
 */
export function flowMessageText(t, message, resendIn = 0) {
    if (!message) return null;

    switch (message.kind) {
        case "invalid":
            return typeof message.attemptsLeft === "number"
                ? t("verification.errors.invalid", { count: message.attemptsLeft })
                : t("verification.errors.invalidNoCount");
        case "expired":
            return t("verification.errors.expired");
        case "rateLimited":
            return t("verification.errors.rateLimited", { seconds: resendIn });
        case "channelNotAvailable":
            return t("verification.errors.channelNotAvailable");
        case "sendFailed":
            return t("verification.errors.sendFailed");
        case "verifyFailed":
            return t("verification.errors.verifyFailed");
        case "phoneVerifyFailed":
            return t("verification.errors.phoneVerifyFailed");
        case "grantRejected":
            return t("verification.info.grantRejected");
        default:
            return null;
    }
}

/**
 * The flow's one line of feedback. Errors go through ErrorMsg; informational
 * notes (a previous grant that expired) are a muted caption. Not a live region
 * itself: the step that renders it decides what gets announced, so it never
 * nests a second `role="status"` inside the step's own.
 *
 * @param {{kind: string, tone: 'error'|'info', attemptsLeft?: number}} message
 * @param {number} [resendIn] live countdown, for the rate-limit message
 */
export default function FlowMessage({ message, resendIn = 0 }) {
    const { t } = useTranslation("common");

    const text = flowMessageText(t, message, resendIn);
    if (!text) return null;

    if (message.tone === "info") {
        return (
            <Text.caption contrast={6} left={<IconInfoCircle />}>
                {text}
            </Text.caption>
        );
    }

    return <ErrorMsg className="verification-message">{text}</ErrorMsg>;
}
